import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';

const EFFECTIVE_DATE = 'June 25, 2026'

interface SectionProps {
  id: string
  title: string
  children: React.ReactNode
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} style={{ scrollMarginTop: '6rem', marginBottom: '2rem' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #f1f5f9' }}>
        {title}
      </h2>
      <div style={{ color: '#64748b', lineHeight: 1.7 }}>{children}</div>
    </section>
  )
}

// @ts-expect-error - Reserved for future table of contents feature
const toc = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'services', label: 'Services' },
  { id: 'client-responsibilities', label: 'Client Responsibilities' },
  { id: 'acceptable-use', label: 'Acceptable Use' },
  { id: 'sms-and-communications', label: 'SMS & Communications' },
  { id: 'fees-and-payments', label: 'Fees & Payments' },
  { id: 'medical-disclaimer', label: 'Medical Disclaimer' },
  { id: 'intellectual-property', label: 'Intellectual Property' },
  { id: 'limitation-of-liability', label: 'Limitation of Liability' },
  { id: 'third-party-services', label: 'Third-Party Services' },
  { id: 'cancellation', label: 'Cancellation Policy' },
  { id: 'disclaimer', label: 'Disclaimer' },
  { id: 'governing-law', label: 'Governing Law' },
  { id: 'changes-to-terms', label: 'Changes to Terms' },
  { id: 'contact', label: 'Contact Information' },
]

export default function TermsAndConditions() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, white)', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Helmet>
        <title>Terms and Conditions | Atomic Tattoo Removal</title>
        <meta name="description" content="Terms and Conditions for Atomic Tattoo Removal. Read our terms for using our website, services, and communications." />
      </Helmet>

      {/* Minimal nav */}
      <header style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1.5rem', height: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '1.125rem', color: '#1e293b', textDecoration: 'none' }}>
            Atomic Tattoo Removal
          </Link>
          <Link
            to="/"
            style={{ fontSize: '0.875rem', color: '#64748b', textDecoration: 'none' }}
          >
            &larr; Back to Home
          </Link>
        </div>
      </header>

      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '4rem 1.5rem' }}>
        {/* Page header */}
        <div style={{ marginBottom: '3rem', maxWidth: '42rem' }}>
          <span style={{ display: 'inline-block', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', color: '#6366f1', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
            Legal
          </span>
          <h1 style={{ fontSize: '2.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '1rem', lineHeight: 1.2 }}>
            Terms and Conditions
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Effective Date:{' '}
            <span style={{ fontWeight: 500, color: '#475569' }}>{EFFECTIVE_DATE}</span>
          </p>
          <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '1rem', lineHeight: 1.7 }}>
            Please read these Terms and Conditions carefully before accessing or using Atomic Tattoo Removal services. By using our services, you agree to be bound by these
            Terms.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4rem' }}>
          {/* Main content */}
          <main style={{ maxWidth: '50rem' }}>
            {/* 1. Introduction */}
            <Section id="introduction" title="1. Introduction">
              <p>
                These Terms and Conditions ("Terms") govern your access to and use of the
                Atomic Tattoo Removal services and website located at{' '}
                <a
                  href="https://atomictattooremoval.com"
                  style={{ color: '#6366f1', textDecoration: 'underline' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://atomictattooremoval.com
                </a>{' '}
                (collectively, the "Services"), operated by Atomic Tattoo Removal ("we," "us," or
                "our").
              </p>
              <p>
                By booking a consultation, receiving treatment, or otherwise accessing
                the Services, you ("Client," "you," or "your") agree to be bound by these
                Terms. If you do not agree, you may not use the Services.
              </p>
            </Section>

            {/* 2. Services */}
            <Section id="services" title="2. Services">
              <p>
                Atomic Tattoo Removal provides professional laser tattoo removal services to clients in Wrentham, MA and surrounding areas. The Services currently include:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Free consultations</span>
                  — initial evaluation of your tattoo and discussion of treatment options, expected outcomes, and pricing.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Laser tattoo removal treatments</span>
                  — professional removal sessions using industry-standard laser technology.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Treatment planning</span>
                  — customized treatment plans based on your tattoo's characteristics and your goals.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Follow-up communications</span>
                  — appointment reminders, aftercare instructions, and progress updates via phone, SMS, or email.
                </li>
              </ul>
              <p>
                We reserve the right to modify, update, or discontinue aspects of the Services at any time, with reasonable notice to active clients where practicable.
              </p>
            </Section>

            {/* 3. Client Responsibilities */}
            <Section id="client-responsibilities" title="3. Client Responsibilities">
              <p>
                When you schedule a consultation or treatment with Atomic Tattoo Removal, you represent and warrant
                that:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Accurate information</span>
                  — All information you provide is accurate, current, and complete. You agree to update your information promptly if it changes.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Health disclosure</span>
                  — You will disclose any relevant medical conditions, medications, or health concerns that may affect your treatment.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Compliance with instructions</span>
                  — You will follow all pre-treatment and aftercare instructions provided by our staff.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Lawful use</span>
                  — You will use the Services only for lawful purposes and in a manner
                  consistent with these Terms.
                </li>
              </ul>
            </Section>

            {/* 4. Acceptable Use */}
            <Section id="acceptable-use" title="4. Acceptable Use">
              <p>
                Your use of Atomic Tattoo Removal services must comply with these Terms and all applicable
                laws. You agree{' '}
                <span style={{ fontWeight: 500, color: '#475569' }}>not</span> to:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Provide false, misleading, or fraudulent information.
                </li>
                <li>
                  Use our services in connection with any unlawful activity.
                </li>
                <li>
                  Harass, threaten, defame, or engage in abusive communication with our staff or other clients.
                </li>
                <li>
                  Attempt to interfere with, compromise, or damage our systems, website, or equipment.
                </li>
                <li>
                  Copy, reproduce, or distribute our proprietary content without authorization.
                </li>
              </ul>
              <p>
                We reserve the right to refuse service or terminate our relationship with any client who violates this Acceptable Use policy.
              </p>
            </Section>

            {/* 5. SMS and Communications */}
            <Section id="sms-and-communications" title="5. SMS & Communications">
              <p>
                Atomic Tattoo Removal sends SMS text messages only to clients who have opted in to receive them. You may opt in by:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Checking the "Subscribe to SMS Appointment Reminders" box on our patient intake form when booking an appointment; or
                </li>
                <li>
                  Texting our business phone number, which constitutes your consent to receive SMS replies related to your inquiry, appointment, or treatment.
                </li>
              </ul>
              <p style={{ marginTop: '1rem' }}>
                By opting in, you consent to receive SMS messages from Atomic Tattoo Removal in accordance with applicable law. Types of messages you may receive include:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Appointment reminders, confirmations, and notices of scheduling changes.
                </li>
                <li>
                  Replies to questions you send us, treatment-related communications, and aftercare instructions.
                </li>
              </ul>

              <div style={{ marginTop: '1.25rem', borderRadius: '0.75rem', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '1.25rem', fontSize: '0.875rem' }}>
                <p style={{ fontWeight: 600, color: '#475569', fontSize: '1rem', marginBottom: '0.75rem' }}>
                  SMS Program Disclosures
                </p>
                <p>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Message frequency varies</span>{' '}
                  depending on your appointments and interactions with our clinic.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  <span style={{ fontWeight: 500, color: '#475569' }}>
                    Message and data rates may apply
                  </span>{' '}
                  based on your mobile carrier plan.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  To <span style={{ fontWeight: 500, color: '#475569' }}>opt out</span>, reply{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>STOP</span>{' '}
                  to any message. A single confirmation will be sent and no further messages will
                  follow.
                </p>
                <p style={{ marginTop: '0.75rem' }}>
                  For <span style={{ fontWeight: 500, color: '#475569' }}>assistance</span>, reply{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#6366f1' }}>HELP</span> to any
                  message or contact us at{' '}
                  <a
                    href="mailto:craig@atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                  >
                    craig@atomictattooremoval.com
                  </a>
                  .
                </p>
              </div>

              <p style={{ marginTop: '1rem' }}>
                Consent to receive SMS messages is not a condition of receiving any service. You may opt out at any time without affecting your ability to book or receive treatment.
              </p>
            </Section>

            {/* 6. Fees and Payments */}
            <Section id="fees-and-payments" title="6. Fees & Payments">
              <p>
                Pricing for tattoo removal services varies based on the size, color, and complexity of your tattoo. Fees are discussed during your free consultation.
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Payment terms</span>
                  — Payment is due at the time of service unless alternative arrangements have been made in advance.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Pricing changes</span>
                  — We reserve the right to modify pricing with reasonable advance notice. Pricing locked in for existing treatment plans will be honored.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Payment methods</span>
                  — We accept cash, credit cards, and other payment methods as posted at our clinic.
                </li>
              </ul>
            </Section>

            {/* 7. Medical Disclaimer */}
            <Section id="medical-disclaimer" title="7. Medical Disclaimer">
              <p>
                Laser tattoo removal is a cosmetic procedure, and individual results may vary based on many factors including tattoo characteristics, skin type, immune response, and adherence to aftercare instructions.
              </p>
              <p>
                <span style={{ fontWeight: 500, color: '#475569' }}>By receiving treatment, you acknowledge that:</span>
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Complete removal cannot be guaranteed, and results depend on multiple factors outside our control.
                </li>
                <li>
                  Multiple treatment sessions are typically required to achieve optimal results.
                </li>
                <li>
                  Temporary side effects such as redness, swelling, blistering, or discomfort may occur.
                </li>
                <li>
                  You have disclosed all relevant health information and understand the risks and benefits of the procedure.
                </li>
              </ul>
              <p>
                This website and our communications do not constitute medical advice. You should consult a qualified healthcare professional regarding your specific medical situation.
              </p>
            </Section>

            {/* 8. Intellectual Property */}
            <Section id="intellectual-property" title="8. Intellectual Property">
              <p>
                All website content, branding, logos, designs, photos, videos, text, and other materials associated with Atomic Tattoo Removal are our exclusive property and are protected by applicable intellectual property laws.
              </p>
              <p>
                You may not copy, reproduce, distribute, publicly display, or create derivative works from our content without express written permission.
              </p>
            </Section>

            {/* 9. Limitation of Liability */}
            <Section id="limitation-of-liability" title="9. Limitation of Liability">
              <p>
                To the fullest extent permitted by applicable law, Atomic Tattoo Removal shall not
                be liable for any indirect, incidental, special, consequential, or punitive
                damages arising out of or related to your use of our Services. This includes, without limitation:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>Treatment outcomes that do not meet your expectations.</li>
                <li>Temporary discomfort, scarring, or other side effects inherent to the procedure.</li>
                <li>
                  Delays or issues caused by third-party communication or payment processing providers.
                </li>
                <li>
                  Loss of data, business interruption, or other indirect damages.
                </li>
              </ul>
              <p>
                Some jurisdictions do not allow certain limitations of liability. If these
                limitations are restricted in your jurisdiction, they apply to the maximum
                extent permitted by law.
              </p>
            </Section>

            {/* 10. Third-Party Services */}
            <Section id="third-party-services" title="10. Third-Party Services">
              <p>
                Atomic Tattoo Removal relies on third-party service providers to deliver certain aspects of our Services. These may include:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>SMS and telephony providers</span>
                  — for facilitating text message and call communications.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Payment processors</span>
                  — for securely processing credit card and other payments.
                </li>
                <li>
                  <span style={{ fontWeight: 500, color: '#475569' }}>Website hosting providers</span>
                  — for maintaining our online presence.
                </li>
              </ul>
              <p>
                Atomic Tattoo Removal is not responsible for the availability, performance, or
                policies of any third-party provider. Service interruptions or issues
                originating with third-party providers are outside our direct control.
              </p>
            </Section>

            {/* 11. Cancellation */}
            <Section id="cancellation" title="11. Cancellation Policy">
              <p>
                We understand that schedules change. If you need to cancel or reschedule an appointment:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Please provide at least 24 hours' notice to avoid a cancellation fee.
                </li>
                <li>
                  Late cancellations or no-shows may result in a fee or forfeiture of prepaid amounts.
                </li>
                <li>
                  We reserve the right to refuse future appointments to clients with a pattern of late cancellations or no-shows.
                </li>
              </ul>
            </Section>

            {/* 12. Disclaimer */}
            <Section id="disclaimer" title="12. Disclaimer">
              <p>
                Our Services are provided <span style={{ fontStyle: 'italic' }}>"as is"</span> and{' '}
                <span style={{ fontStyle: 'italic' }}>"as available"</span> without
                warranties of any kind, express or implied. While we strive to provide safe, effective, high-quality services, we do not warrant that:
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', marginTop: '0.75rem', lineHeight: 1.8 }}>
                <li>
                  Treatment will result in complete tattoo removal or meet your specific expectations.
                </li>
                <li>
                  Services will be uninterrupted, error-free, or free from technical issues.
                </li>
                <li>
                  All side effects or complications can be anticipated or prevented.
                </li>
              </ul>
              <p>
                To the extent permitted by applicable law, we disclaim all implied warranties,
                including merchantability and fitness for a particular purpose.
              </p>
            </Section>

            {/* 13. Governing Law */}
            <Section id="governing-law" title="13. Governing Law">
              <p>
                These Terms and any disputes arising out of or related to your use of our Services shall be governed by and construed in accordance with the laws of
                the <span style={{ fontWeight: 500, color: '#475569' }}>Commonwealth of Massachusetts</span>, without regard to its conflict of law provisions.
              </p>
              <p>
                Any legal action or proceeding arising under these Terms shall be brought
                exclusively in the state or federal courts located within the Commonwealth of
                Massachusetts, and you consent to personal jurisdiction in those courts.
              </p>
            </Section>

            {/* 14. Changes to Terms */}
            <Section id="changes-to-terms" title="14. Changes to Terms">
              <p>
                We may update these Terms from time to time to reflect changes in our
                services, applicable law, or business practices. When we make material
                changes, we will update the "Effective Date" at the top of this page and,
                where appropriate, notify active clients via email or an in-person notice.
              </p>
              <p>
                Your continued use of our Services following the effective date of any
                updated Terms constitutes your acceptance of the revised Terms. If you do not
                agree to the updated Terms, you should discontinue use of our Services.
              </p>
            </Section>

            {/* 15. Contact Information */}
            <Section id="contact" title="15. Contact Information">
              <p>
                If you have questions, concerns, or requests regarding these Terms, please
                contact us:
              </p>
              <div style={{ marginTop: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', background: '#f8fafc', padding: '1.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Atomic Tattoo Removal</p>
                <p style={{ marginTop: '0.5rem' }}>
                  <span style={{ color: '#64748b' }}>Address:</span> 78 South St #201, Wrentham, MA 02093
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Phone:</span> (508) 203-1342
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Website:</span>{' '}
                  <a
                    href="https://atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://atomictattooremoval.com
                  </a>
                </p>
                <p>
                  <span style={{ color: '#64748b' }}>Email:</span>{' '}
                  <a
                    href="mailto:craig@atomictattooremoval.com"
                    style={{ color: '#6366f1', textDecoration: 'underline' }}
                  >
                    craig@atomictattooremoval.com
                  </a>
                </p>
              </div>
            </Section>

            {/* Bottom divider */}
            <div style={{ paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9', fontSize: '0.75rem', color: '#cbd5e1' }}>
              &copy; {new Date().getFullYear()} Atomic Tattoo Removal. All rights reserved. &mdash;{' '}
              Effective {EFFECTIVE_DATE}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
